import {
	ModuleActionsSearchContext,
	SearchBarActionsSearchContext,
	SearchResultSearchContext,
	SetSearchContext,
	type ModuleActionsSearch,
	type SearchBarActionsSearch,
	type SetSearch,
} from '@/contexts/search'
import { useFnProxy } from '@lc/libreact'
import { delayedPromise } from '@lc/librunner'
import { use, useCallback, useEffect, useRef } from 'react'
import { useModule } from './useModule'

export function useSearchModuleActions(): {
	current: ModuleActionsSearch | null
	waitForPath: (path: string) => Promise<ModuleActionsSearch>
} {
	const waiting = useRef(
		new Set<{
			path: string
			resolve: (actions: ModuleActionsSearch) => void
		}>(),
	)
	const current = use(ModuleActionsSearchContext)
	const waitForPath = useCallback(
		(path: string) => {
			if (current?.path === path) return Promise.resolve(current)
			const promise = Object.assign(
				delayedPromise<ModuleActionsSearch>(),
				{
					path,
				},
			)
			waiting.current.add(promise)
			promise
				.finally(() => waiting.current.delete(promise))
				.catch(() => void 0)
			setTimeout(() => promise.reject(new Error('timed out')), 5000)
			return promise
		},
		[current],
	)
	useEffect(() => {
		if (!current) return
		const toDelete: (typeof waiting)['current'] = new Set()
		// so we don't infinite loop
		// eslint-disable-next-line unicorn/no-useless-spread
		for (const item of [...waiting.current]) {
			if (item.path === current.path) {
				toDelete.add(item)
			}
		}
		for (const item of toDelete) {
			waiting.current.delete(item)
			item.resolve(current)
		}
	}, [current])
	return { current, waitForPath }
}

const doNothing = () => void 0

export function useSetSearchModuleActions(
	actions: Partial<Omit<ModuleActionsSearch, 'path'>>,
): ModuleActionsSearch {
	const acceptSearch = useFnProxy(actions.acceptSearch ?? doNothing)
	const clearSearch = useFnProxy(actions.clearSearch ?? doNothing)

	const set = use(SetSearchContext)
	if (!set) throw new Error('Outside of SetSearchContext')

	const { path } = useModule()
	useEffect(() => {
		set.setModule({ path, acceptSearch, clearSearch })
	}, [path, acceptSearch, clearSearch, set])

	const result = use(SearchResultSearchContext)
	useEffect(() => {
		if (result) acceptSearch(result)
	}, []) // <- on purpose

	return { path, acceptSearch, clearSearch }
}

export function useSearchBarActions(): SearchBarActionsSearch {
	const searchActions = use(SearchBarActionsSearchContext)

	const clearSearch = useFnProxy(searchActions?.clearSearch ?? doNothing)

	return { clearSearch }
}

export function useSetSearchBarActions(actions: SearchBarActionsSearch): void {
	const clearSearch = useFnProxy(actions?.clearSearch ?? doNothing)

	const set = use(SetSearchContext)
	if (!set) throw new Error('Outside of SetSearchContext')

	useEffect(() => {
		set.setSearch({ clearSearch })
	}, [clearSearch, set])
}

export function useSearchActions(): SetSearch {
	const set = use(SetSearchContext)
	if (!set) throw new Error('Outside of SetSearchContext')

	return set
}
